import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import './Supplements.css';

export default function Supplements() {
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [supplements, setSupplements] = useState([]);
  const [checkoffs, setCheckoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  async function loadData() {
    setLoading(true);

    const { data: clientData } = await supabase
      .from('clients')
      .select('*')
      .eq('auth_user_id', user.id)
      .single();

    if (!clientData) {
      setLoading(false);
      return;
    }
    setClient(clientData);

    const [suppRes, checkRes] = await Promise.all([
      supabase
        .from('supplements')
        .select('*')
        .eq('client_id', clientData.id)
        .eq('is_active', true)
        .order('reminder_time', { ascending: true, nullsFirst: false })
        .order('sort_order'),
      supabase
        .from('supplement_checkoffs')
        .select('*')
        .eq('client_id', clientData.id)
        .eq('date', today),
    ]);

    setSupplements(suppRes.data || []);
    setCheckoffs(checkRes.data || []);
    setLoading(false);
  }

  async function toggleCheckoff(supplementId) {
    const existing = checkoffs.find((c) => c.supplement_id === supplementId);

    if (existing) {
      await supabase.from('supplement_checkoffs').delete().eq('id', existing.id);
      setCheckoffs((prev) => prev.filter((c) => c.id !== existing.id));
    } else {
      const { data } = await supabase
        .from('supplement_checkoffs')
        .insert({
          client_id: client.id,
          supplement_id: supplementId,
          date: today,
        })
        .select()
        .single();

      if (data) {
        setCheckoffs((prev) => [...prev, data]);
      }
    }
  }

  function isChecked(supplementId) {
    return checkoffs.some((c) => c.supplement_id === supplementId);
  }

  function formatTime(time) {
    if (!time) return '';
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${m} ${ampm}`;
  }

  // Group supplements by time bucket
  function getTimeBucket(time) {
    if (!time) return 'Anytime';
    const hour = parseInt(time.split(':')[0], 10);
    if (hour < 12) return 'Morning';
    if (hour < 17) return 'Afternoon';
    return 'Evening';
  }

  function groupSupplements() {
    const bucketOrder = ['Morning', 'Afternoon', 'Evening', 'Anytime'];
    const buckets = {};

    supplements.forEach((supp) => {
      const bucket = getTimeBucket(supp.reminder_time);
      if (!buckets[bucket]) buckets[bucket] = [];
      buckets[bucket].push(supp);
    });

    return bucketOrder
      .filter((b) => buckets[b]?.length > 0)
      .map((b) => ({ label: b, items: buckets[b] }));
  }

  const completed = checkoffs.length;
  const total = supplements.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  if (loading) {
    return (
      <div className="screen container">
        <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--color-text-muted)' }}>
          Loading supplements...
        </div>
      </div>
    );
  }

  const dayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  const grouped = groupSupplements();

  return (
    <div className="screen container supp-screen">
      <div className="supp-page-header">
        <h1>Supplements</h1>
        <p className="supp-page-date">{dayLabel}</p>
      </div>

      {/* Progress */}
      {total > 0 && (
        <div className="supp-progress-card">
          <div className="supp-progress-info">
            <span className="supp-progress-label">Today's Supplements</span>
            <span className="supp-progress-count">
              {completed} of {total}
            </span>
          </div>
          <div className="supp-progress-bar">
            <div className="supp-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          {pct === 100 && total > 0 && (
            <p className="supp-progress-done">All supplements taken today!</p>
          )}
        </div>
      )}

      {/* Supplement list */}
      {supplements.length === 0 ? (
        <div className="supp-page-empty">
          <p>💊</p>
          <p>No supplements assigned yet.</p>
          <p className="supp-page-empty-hint">Your practitioner will add them when ready.</p>
        </div>
      ) : (
        <div className="supp-groups">
          {grouped.map((group) => (
            <div key={group.label} className="supp-group">
              <h3 className="supp-group-label">{group.label}</h3>
              <div className="supp-items">
                {group.items.map((supp) => {
                  const checked = isChecked(supp.id);
                  return (
                    <button
                      key={supp.id}
                      className={`supp-item ${checked ? 'supp-item-done' : ''}`}
                      onClick={() => toggleCheckoff(supp.id)}
                    >
                      <div className={`supp-check ${checked ? 'supp-check-on' : ''}`}>
                        {checked && (
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </div>
                      <div className="supp-item-info">
                        <div className="supp-item-name">
                          <span className="supp-item-icon">💊</span>
                          {supp.name}
                        </div>
                        {(supp.dosage || supp.instructions) && (
                          <div className="supp-item-detail">
                            {supp.dosage && <span className="supp-item-dosage">{supp.dosage}</span>}
                            {supp.dosage && supp.instructions && <span> · </span>}
                            {supp.instructions && <span>{supp.instructions}</span>}
                          </div>
                        )}
                      </div>
                      {supp.reminder_time && (
                        <span className="supp-item-time">{formatTime(supp.reminder_time)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
